import { useCallback, useEffect, useState } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  Box,
  Icon,
  Items,
  ListItem,
  Loading,
  LoadingDiv,
  LoadingWrap,
  ModelBox,
  ModelHeader,
  ProjectList,
  SearchInput,
  Text,
  Title
} from '../searchRepositoryModel/styled';
import { useDispatch, useSelector } from 'react-redux';
import { getVercelProjectList } from '../../../services/vercelService';
import { VercelProject } from '../../../mobx/interfaces/project';
import { isEmpty } from '../../../helpers/common';
import EmptyState from '../../emptyState';
import projectempty from '../../../assets/images/emptystates/emptyrepolight.svg';
import projectdarkempty from '../../../assets/images/emptystates/emptyrepodark.svg';
import noprojectfound from '../../../assets/images/emptystates/notfoundrepolight.svg';
import noprojectfounddark from '../../../assets/images/emptystates/notfoundrepodark.svg';
import { selectThemeMode } from '../../../store/selectors';

interface Props {
  onClose?: () => void;
  handleSelect: (project: VercelProject) => void;
}
function VercelIntegrationModal({ onClose, handleSelect }: Props) {
  //Use state variables
  const [loading, setLoading] = useState(false);
  const [vercelProjects, setVercelProjects] = useState<VercelProject[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<VercelProject[]>([]);

  //Use selector variables
  const themeMode = useSelector(selectThemeMode);

  //Other variables
  const dispatch = useDispatch();
  const getImageSource = () => (themeMode?.theme === 'dark' ? projectdarkempty : projectempty);
  const notFoundProjectSource = () => (themeMode?.theme === 'dark' ? noprojectfounddark : noprojectfound);

  //Get load vercel project list
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const vercelProjectList = await dispatch(getVercelProjectList());
      setVercelProjects(vercelProjectList || []);
      setFilteredProjects(vercelProjectList || []);
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  //Handle repository search
  const handleSearch = useCallback(
    (value: string) => {
      const searchNewText = value.trim().toLowerCase();
      if (searchNewText === '') {
        setFilteredProjects(vercelProjects);
        return;
      }
      const words = searchNewText.split(' ').filter((word) => word.length > 0);
      const updatedList = vercelProjects?.filter((item) => {
        const title = item?.name.trim().toLowerCase() || '';
        return words.some((word) => title.includes(word));
      });
      setFilteredProjects(updatedList);
    },
    [vercelProjects]
  );

  return (
    <ModelBox>
      <ModelHeader>
        <SearchInput
          type='text'
          placeholder='Search vercel projects...'
          onChange={(e) => handleSearch(e.target.value)}
        />
        <Icon onClick={onClose}>
          <SVGIcon name='close-icon' width='18' height='18' viewBox='0 0 18 18' fill='none' className='close-icon' />
        </Icon>
      </ModelHeader>
      <ProjectList>
        {!loading && !isEmpty(vercelProjects) && !isEmpty(filteredProjects) && <Title>Projects</Title>}
        {!loading && isEmpty(vercelProjects) && (
          <Box>
            <EmptyState header='No Projects' isGap={16} image={getImageSource()} hideButton isCustomerDetail={true} />
          </Box>
        )}
        {loading && (
          <Box>
            <LoadingDiv>
              <LoadingWrap>
                <Loading />
              </LoadingWrap>
              <Text>Loading...</Text>
            </LoadingDiv>
          </Box>
        )}
        {!loading && !isEmpty(vercelProjects) && isEmpty(filteredProjects) && (
          <Box>
            <EmptyState
              header='No search results found.'
              title='Try changing your search term.'
              isGap={16}
              image={notFoundProjectSource()}
              hideButton
              isCustomerDetail={true}
            />
          </Box>
        )}
        {!loading && !isEmpty(vercelProjects) && !isEmpty(filteredProjects) && (
          <Items>
            {filteredProjects.map((project) => (
              <ListItem key={project.id} onClick={() => handleSelect(project)}>
                <p>{project.name}</p>
              </ListItem>
            ))}
          </Items>
        )}
      </ProjectList>
    </ModelBox>
  );
}
export default VercelIntegrationModal;
